/**
 * The nodes the signed-in person belongs to across this deployment
 * (GET /api/nodes/mine, backend services/nodes.memberships): what the node
 * switcher lists, and where "the platform node" is when a page needs to send
 * someone there. Each row: { slug, name, url, is_platform, is_current, role }.
 *
 * Cached through the api client (30 s TTL, in-flight dedup) because the nav
 * bar mounts on every page; a signup invalidates it. A backend without the
 * route (or a signed-out visitor) yields an empty list, never an error.
 *
 *   const { nodes, current, platform, reload } = useMyNodes();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';

export function useMyNodes() {
    const api = useApi();
    const { isAuthenticated } = useAuth();
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        if (!isAuthenticated) { setNodes([]); return Promise.resolve(); }
        setLoading(true);
        return api.get('/api/nodes/mine', { cache: true })
            .then(data => setNodes(Array.isArray(data) ? data : []))
            .catch(() => setNodes([]))
            .finally(() => setLoading(false));
    }, [api, isAuthenticated]);

    useEffect(() => { load(); }, [load]);

    return {
        nodes,
        loading,
        reload: load,
        current: nodes.find(n => n.is_current) || null,
        platform: nodes.find(n => n.is_platform) || null,
    };
}

export default useMyNodes;
